/**
 * Die Rohzeilen hinter einer Kassensession — das, was in den Quelldateien steht.
 *
 * Zum Nachprüfen, nicht zum Verstehen: die Erklärung steht darüber in
 * `SessionPanel`. Hier geht es nur darum, jede angezeigte Zahl bis in die
 * Ursprungsdatei zurückverfolgen zu können.
 */
import type { CashSessionDto } from "../lib/api";
import { euro } from "../lib/format";
import { Amount, Info } from "./ui";

export function Receipts({ session }: { session: CashSessionDto }) {
  const receipts = session.receipts ?? [];
  if (receipts.length === 0) {
    return (
      <p className="px-4 py-2 text-xs text-muted">
        Keine Belege verknüpft. Der Wert wurde von Hand erfasst.
      </p>
    );
  }

  return (
    <div className="px-4 py-3">
      <table className="w-full max-w-3xl text-xs">
        <thead>
          <tr className="text-muted">
            <th className="py-1 text-left font-normal">
              Woher
              <Info text="Kassenabschluss kommt aus ecos, Terminal-Auslesung aus dem VR-Export. Beide Geräte protokollieren getrennt; der Abgleich bringt sie zusammen." />
            </th>
            <th className="py-1 text-left font-normal">
              Kennung
              <Info text="Die Belegnummer in der Quelldatei. Damit lässt sich die Zeile dort wiederfinden." />
            </th>
            <th className="py-1 text-left font-normal">Zeitraum</th>
            <th className="py-1 text-right font-normal">Betrag</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((r, i) => (
            <tr key={`${r.art}-${r.kennung}-${i}`} className="border-t border-line">
              <td className="py-1.5">
                <span className="chip-muted">
                  {r.art === "ABSCHLUSS" ? "Kassenabschluss" : "Terminal-Auslesung"}
                </span>
                {r.manuell ? (
                  <span className="chip-warn ml-1" title="Dieser Wert wurde von Hand erfasst.">
                    von Hand
                  </span>
                ) : null}
              </td>
              <td className="py-1.5 font-mono text-muted">{r.kennung}</td>
              <td className="py-1.5 text-muted">
                {r.von ? (
                  <>
                    {stamp(r.von)} <span className="text-muted">bis</span> {stamp(r.zeitpunkt)}
                  </>
                ) : (
                  <>ausgelesen {stamp(r.zeitpunkt)}</>
                )}
              </td>
              <td className="py-1.5">
                <Amount value={euro(r.betragKorrigiert ?? r.betrag)} />
                {r.betragKorrigiert ? (
                  <div className="text-[10px] text-muted line-through">{euro(r.betrag)}</div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[11px] text-muted">
        Ein Kassenabschluss läuft von einem Abschluss bis zum nächsten, eine Terminal-Auslesung
        gilt für alles seit der letzten. Beide Beträge sind kumulativ. Deshalb rechnet die App
        je Kasse und Betriebstag, nie je Beleg.
      </p>
    </div>
  );
}

function stamp(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
